/**
 * Общая раскладка административных экранов (§9): заголовок 20/28, строка-мета,
 * кнопка-действие справа, ниже — таблица строк 36 px. Боковых панелей нет.
 *
 * Отдельным компонентом, потому что все пять экранов (§9.1–§9.5) обязаны
 * выглядеть одинаково: шапка не должна «ехать» при переходе между разделами.
 */
import { useRef, useState, type ReactNode } from 'react'
import { Button, IconButton, Menu, type MenuItem } from '../../components/index.js'
import { adminTexts } from './texts.admin.js'
import './admin.css'

export interface AdminPageProps {
  /** Экран с таблицей: ему отдаётся вся ширина окна, а не колонка под формы. */
  wide?: boolean
  title: string
  /** Строка-пояснение под заголовком: механика раздела. */
  subtitle?: ReactNode
  /** Число записей рядом с заголовком: пустых состояний без числа не бывает (§11.2). */
  meta?: ReactNode
  /** Главное действие раздела: «+ Категория», «+ Пользователь». */
  action?: ReactNode
  children: ReactNode
}

export function AdminPage({ title, subtitle, meta, action, wide = false, children }: AdminPageProps) {
  return (
    <div className={wide ? 'adm adm--wide' : 'adm'}>
      <div className="adm__head">
        <div className="adm__titles">
          <h1 className="t-h1">{title}</h1>
          {meta ? <p className="adm__meta t-meta">{meta}</p> : null}
        </div>
        {action ? <div className="adm__action">{action}</div> : null}
      </div>
      {subtitle ? <p className="adm__sub t-meta">{subtitle}</p> : null}
      <div className="adm__body">{children}</div>
    </div>
  )
}

/** Группа настроек: заголовок 16/22, рамка, вертикальный ритм полей (§9.5). */
export function AdminGroup({
  title, lead, children, footer,
}: {
  title: string
  lead?: ReactNode
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <section className="adm-group" aria-label={title}>
      <h2 className="adm-group__title t-h2">{title}</h2>
      {lead ? <p className="adm-group__lead t-meta">{lead}</p> : null}
      <div className="adm-group__body">{children}</div>
      {footer ? <div className="adm-group__footer">{footer}</div> : null}
    </section>
  )
}

export interface RowActionsProps {
  /** Доступное имя меню: «Действия с категорией Холодильники». */
  label: string
  items: MenuItem[]
  onSelect: (id: string) => void
}

/** Кнопка «⋯» в строке таблицы вместе со своим меню (§9: колонка 40 px). */
export function RowActions({ label, items, onSelect }: RowActionsProps) {
  const anchor = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)

  return (
    <>
      <IconButton
        ref={anchor}
        label={label}
        icon={<IconEllipsis />}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      />
      <Menu
        open={open}
        anchor={anchor.current}
        items={items}
        label={label}
        onClose={() => setOpen(false)}
        onSelect={(id) => {
          setOpen(false)
          onSelect(id)
        }}
      />
    </>
  )
}

/** Три точки: в общем наборе иконок такой нет, а колонка «⋯» есть во всех §9. */
export function IconEllipsis() {
  return (
    <svg className="icon" width="16" height="16" viewBox="0 0 20 20" fill="currentColor"
      focusable="false" aria-hidden="true">
      <circle cx="4.5" cy="10" r="1.4" />
      <circle cx="10" cy="10" r="1.4" />
      <circle cx="15.5" cy="10" r="1.4" />
    </svg>
  )
}

/** Строка-причина под недоступным действием (§11.4): кнопка не исчезает. */
export function AdminReason({ children }: { children: ReactNode }) {
  return <p className="adm-reason t-meta">{children}</p>
}

/** Ошибка загрузки раздела (§11.3): текст сервера и «Повторить». */
export function AdminError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="adm-error" role="alert">
      <p className="t-body">{adminTexts.common.loadFailed}</p>
      <p className="adm-error__text t-meta">{message}</p>
      <div className="adm-error__actions">
        <Button variant="secondary" onClick={onRetry}>{adminTexts.common.retry}</Button>
      </div>
    </div>
  )
}
